<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';

import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getSidebarStructure } from 'dashboard/constants/sidebarStructure';
import { DEFAULT_ROLE_PERMISSIONS } from 'dashboard/constants/permissions.js';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { shouldShow } = usePolicy();

const accountId = computed(() => store.getters.getCurrentAccountId);
const account = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const isUpdating = computed(
  () => store.getters['accounts/getUIFlags'].isUpdating
);

const getAccountPermissions = computed(
  () => account.value?.settings?.role_permissions || DEFAULT_ROLE_PERMISSIONS
);

const draftPermissions = ref({});

const DYNAMIC_SIDEBAR_ROUTE_MAP = {
  custom_views: 'folder_conversations',
  teams: 'team_conversations',
  inboxes: 'inbox_dashboard',
  labels: 'label_conversations',
  contact_custom_views: 'contacts_dashboard_segments_index',
  labels_contacts: 'contacts_dashboard_labels_index',
};

const findRouteByName = routeName => {
  if (!routeName) return null;
  return router.getRoutes().find(route => route.name === routeName);
};

const isSidebarOptionVisibleToAdmin = option => {
  if (!option) return false;

  const routeName =
    option.navigationPath ||
    option.toName ||
    (option.isDynamic ? DYNAMIC_SIDEBAR_ROUTE_MAP[option.dynamicType] : null);

  if (routeName) {
    const route = findRouteByName(routeName);
    const permissions = route?.meta?.permissions || [];
    const featureFlag = route?.meta?.featureFlag || '';
    const installationTypes = route?.meta?.installationTypes || [];
    return shouldShow(featureFlag, permissions, installationTypes);
  }

  if (option.children?.length) {
    return option.children.some(child => isSidebarOptionVisibleToAdmin(child));
  }

  return true;
};

watch(
  getAccountPermissions,
  newVal => {
    draftPermissions.value = JSON.parse(JSON.stringify(newVal));
  },
  { immediate: true, deep: true }
);

const sidebarFeatures = computed(() => {
  const structure = getSidebarStructure(t);
  const features = [];

  structure.forEach(item => {
    if (!isSidebarOptionVisibleToAdmin(item)) return;

    if (
      item.key === 'sales_assistant' &&
      account.value?.settings?.sales_assist_enabled === 'inactive'
    ) {
      return;
    }

    if (item.key) {
      features.push({ key: item.key, label: item.label, isParent: true });
    }

    if (item.children) {
      item.children.forEach(child => {
        if (!isSidebarOptionVisibleToAdmin(child)) return;
        features.push({
          key: child.key,
          label: child.label,
          isParent: false,
          parentKey: item.key,
        });
      });
    }
  });

  return features;
});

const LOCKED_ROLE_FEATURES = ['all_conversations', 'conversation', 'inbox'];

const isFeatureLocked = featureKey => LOCKED_ROLE_FEATURES.includes(featureKey);

const childFeaturesByParent = computed(() => {
  return sidebarFeatures.value.reduce((acc, feature) => {
    if (feature.parentKey) {
      acc[feature.parentKey] = [...(acc[feature.parentKey] || []), feature.key];
    }
    return acc;
  }, {});
});

const featureByKey = computed(() => {
  return sidebarFeatures.value.reduce((acc, feature) => {
    acc[feature.key] = feature;
    return acc;
  }, {});
});

const getRelatedFeatureKeys = featureKey => {
  const childKeys = childFeaturesByParent.value[featureKey] || [];
  return [featureKey, ...childKeys];
};

const isFeatureEnabled = (role, featureKey) => {
  const rolePermissions = draftPermissions.value[role] || [];
  const feature = featureByKey.value[featureKey];

  if (!feature?.isParent) {
    if (isFeatureLocked(featureKey)) return true;
    return rolePermissions.includes(featureKey);
  }

  const featureKeys = getRelatedFeatureKeys(featureKey);
  return featureKeys.every(
    key => isFeatureLocked(key) || rolePermissions.includes(key)
  );
};

const togglePermission = (role, featureKey) => {
  if (isFeatureLocked(featureKey)) return;

  const rolePermissions = draftPermissions.value[role] || [];
  const feature = featureByKey.value[featureKey];

  if (!feature?.isParent) {
    draftPermissions.value[role] = rolePermissions.includes(featureKey)
      ? rolePermissions.filter(key => key !== featureKey)
      : [...new Set([...rolePermissions, featureKey])];
    return;
  }

  const featureKeys = getRelatedFeatureKeys(featureKey).filter(
    key => !isFeatureLocked(key)
  );
  const allEnabled = featureKeys.every(key => rolePermissions.includes(key));

  draftPermissions.value[role] = allEnabled
    ? rolePermissions.filter(key => !featureKeys.includes(key))
    : [...new Set([...rolePermissions, ...featureKeys])];
};

const updatePermissions = async () => {
  try {
    await store.dispatch('accounts/update', {
      role_permissions: draftPermissions.value,
    });
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full m-0 p-6 sm:py-8 lg:px-16 overflow-auto bg-n-background font-inter"
  >
    <div class="flex items-start w-full max-w-6xl mx-auto">
      <SettingsLayout class="w-full">
        <template #header>
          <BaseSettingsHeader
            :title="$t('SIDEBAR.ROLE_MANAGEMENT')"
            :description="$t('SIDEBAR.ROLE_MANAGEMENT_DESCRIPTION')"
          >
            <template #actions>
              <NextButton
                blue
                :is-loading="isUpdating"
                @click="updatePermissions"
              >
                {{ $t('GENERAL_SETTINGS.SUBMIT') }}
              </NextButton>
            </template>
          </BaseSettingsHeader>
        </template>

        <template #body>
          <div
            class="mt-6 h-[42rem] overflow-auto text-n-slate-11 border border-n-weak rounded-md"
          >
            <table class="min-w-full divide-y divide-n-weak rounded-md">
              <thead
                class="border-b border-n-weak sticky top-[-1px] bg-n-background z-10 px-6"
              >
                <tr>
                  <th
                    class="py-3 px-6 ltr:pr-4 rtl:pl-4 text-left text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('SIDEBAR.ROLE_MANAGEMENT_FEATURE') }}
                  </th>
                  <th
                    class="py-3 px-6 ltr:pr-4 rtl:pl-4 text-center text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('SIDEBAR.ROLE_MANAGEMENT_MANAGER') }}
                  </th>
                  <th
                    class="py-3 px-6 ltr:pr-4 rtl:pl-4 text-center text-sm font-medium text-n-slate-11"
                  >
                    {{ $t('SIDEBAR.ROLE_MANAGEMENT_AGENT') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-11">
                <tr
                  v-for="feature in sidebarFeatures"
                  :key="feature.key"
                  :class="
                    feature.isParent
                      ? 'bg-n-alpha-2'
                      : 'bg-n-alpha-1 transition-colors duration-150 hover:bg-n-alpha-3'
                  "
                >
                  <td class="p-6 ltr:pr-4 rtl:pl-4">
                    <span
                      :class="
                        feature.isParent
                          ? 'font-medium text-n-slate-12'
                          : 'pl-6 text-sm text-n-slate-11'
                      "
                    >
                      {{ feature.label }}
                    </span>
                  </td>

                  <td class="py-3 px-6 ltr:pr-4 rtl:pl-4">
                    <div class="flex justify-center">
                      <input
                        type="checkbox"
                        :disabled="isFeatureLocked(feature.key)"
                        :checked="isFeatureEnabled('manager', feature.key)"
                        class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        @change="togglePermission('manager', feature.key)"
                      />
                    </div>
                  </td>

                  <td class="py-3 px-6 ltr:pr-4 rtl:pl-4">
                    <div class="flex justify-center">
                      <input
                        type="checkbox"
                        :disabled="isFeatureLocked(feature.key)"
                        :checked="isFeatureEnabled('agent', feature.key)"
                        class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        @change="togglePermission('agent', feature.key)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </SettingsLayout>
    </div>
  </div>
</template>
